import { useEffect, useRef } from "react";
import posthog from "posthog-js";
import { X } from "lucide-react";
import { Mascot } from "@/components/Mascot";
import { cn } from "@/lib/utils";

interface PostVisitReflectionPromptProps {
  cafeName: string;
  cafeId?: string;
  onTap: () => void;
  onDismiss: () => void;
  className?: string;
}

export function PostVisitReflectionPrompt({
  cafeName,
  cafeId,
  onTap,
  onDismiss,
  className,
}: PostVisitReflectionPromptProps) {
  // Analytics only: this component renders only when the ask is on screen.
  const shownRef = useRef(false);
  useEffect(() => {
    if (shownRef.current) return;
    shownRef.current = true;
    try {
      posthog.capture("checkin_prompt_shown", {
        source: "post_visit_reflection",
        cafe_id: cafeId ?? null,
      });
    } catch { /* ignore */ }
  }, [cafeId]);

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDismiss();
  };

  return (
    <div
      className={cn(
        "fixed bottom-0 left-0 right-0 z-50 p-4 pb-[calc(1rem+env(safe-area-inset-bottom)+4rem)] lg:pb-[calc(1rem+env(safe-area-inset-bottom))]",
        "animate-slide-up",
        className
      )}
    >
      <div
        onClick={onTap}
        className={cn(
          "relative mx-auto max-w-md cursor-pointer",
          "rounded-xl bg-card border border-border shadow-lg",
          "p-4 pr-10",
          "transition-all duration-200 hover:shadow-xl hover:border-primary/30"
        )}
      >
        {/* Dismiss button */}
        <button
          onClick={handleDismiss}
          className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-muted/50 text-muted-foreground/60 hover:text-muted-foreground transition-colors"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>

        {/* Content */}
        <div className="flex items-center gap-3">
          <Mascot mood="happy" size="sm" className="shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="font-medium text-foreground text-sm leading-tight truncate">
              How was your visit to {cafeName}?
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Tap to share your experience
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
